import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { Subscription, concat, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IssueReportArea } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/App/issue-report-area';
import { IssueReportSeverity } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/App/issue-report-severity';
import { MxMenuService } from 'src/app/core/services/mx.menu.service';
import { MxNotificationService, MxNotificationSeverity } from 'src/app/core/services/mx.notification.service';
import { SzIssueReportDialogService, SzIssueReportPrefill } from 'src/app/core/services/sz-issue-report-dialog.service';
import {
  AppIssueReportService,
  ISSUE_ATTACHMENT_MAX_BYTES,
  ISSUE_ATTACHMENT_MAX_COUNT,
  ISSUE_ATTACHMENT_TYPES,
  ISSUE_DESCRIPTION_MAX,
  ISSUE_TITLE_MAX,
} from 'src/app/features/services/app-issue-report.service';
import { loyaltyErrorBody } from 'src/app/features/services/loyalty-errors';
import { SzPillOption } from '../sz-ui.components';

/** Kiválasztott csatolmány a feltöltés előtt: a kép előnézete objektum-URL, a PDF ikon. */
interface PendingAttachment {
  file: File;
  previewUrl: string | null;
}

interface PageOption {
  label: string;
  value: string;
}

/**
 * „Hiba bejelentése” dialógus: terület (Portál / Szirom Pont app), oldal vagy képernyő, cím, leírás, súlyosság,
 * legfeljebb 3 kép vagy PDF. A beküldés két lépés: a bejelentés létrejön, utána a csatolmányok egyenként
 * töltődnek fel; egy sikertelen fájl nem veszi el a bejelentést, csak érthető üzenetet ad.
 * Egy példány él a vázban, az SzIssueReportDialogService nyitja.
 */
@Component({
  standalone: false,
  selector: 'sz-issue-report-dialog',
  templateUrl: './sz-issue-report-dialog.component.html',
  styleUrls: ['./sz-issue-report-dialog.component.scss'],
})
export class SzIssueReportDialogComponent implements OnInit, OnDestroy {
  visible = false;
  busy = false;
  submitted = false;

  area: IssueReportArea = IssueReportArea.Portal;
  page = '';
  title = '';
  description = '';
  severity: IssueReportSeverity = IssueReportSeverity.Normal;
  attachments: PendingAttachment[] = [];

  readonly areaOptions: SzPillOption<IssueReportArea>[] = [
    { label: 'Portál', value: IssueReportArea.Portal },
    { label: 'Szirom Pont app', value: IssueReportArea.MobileApp },
  ];
  severityOptions: { label: string; value: IssueReportSeverity; hint: string }[] = [];
  pageOptions: PageOption[] = [];

  readonly titleMax = ISSUE_TITLE_MAX;
  readonly descriptionMax = ISSUE_DESCRIPTION_MAX;
  readonly maxFiles = ISSUE_ATTACHMENT_MAX_COUNT;
  readonly accept = ISSUE_ATTACHMENT_TYPES.join(',');
  IssueReportArea = IssueReportArea;

  private subs = new Subscription();

  constructor(
    private dialogService: SzIssueReportDialogService,
    private issueReportService: AppIssueReportService,
    private notificationService: MxNotificationService,
    private menuService: MxMenuService,
    private i18NextPipe: I18NextPipe,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.severityOptions = [
      { label: this.enumLabel('IssueReportSeverity', IssueReportSeverity[IssueReportSeverity.Low]), value: IssueReportSeverity.Low, hint: 'szépséghiba, nem zavar' },
      { label: this.enumLabel('IssueReportSeverity', IssueReportSeverity[IssueReportSeverity.Normal]), value: IssueReportSeverity.Normal, hint: 'zavar, de van kerülőút' },
      { label: this.enumLabel('IssueReportSeverity', IssueReportSeverity[IssueReportSeverity.High]), value: IssueReportSeverity.High, hint: 'egy funkció nem használható' },
      { label: this.enumLabel('IssueReportSeverity', IssueReportSeverity[IssueReportSeverity.Blocking]), value: IssueReportSeverity.Blocking, hint: 'nem tudok dolgozni' },
    ];
    this.subs.add(this.dialogService.open$.subscribe((prefill) => this.open(prefill)));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.clearAttachments();
  }

  // --- megnyitás, alaphelyzet ---

  open(prefill: SzIssueReportPrefill) {
    this.pageOptions = this.buildPageOptions();
    this.submitted = false;
    this.busy = false;
    this.area = prefill.area ?? IssueReportArea.Portal;
    this.page = this.area === IssueReportArea.Portal ? this.pageLabelFor(prefill.page) : prefill.page ?? '';
    this.title = '';
    this.description = '';
    this.severity = IssueReportSeverity.Normal;
    this.clearAttachments();
    this.visible = true;
  }

  close() {
    if (this.busy) return;
    this.visible = false;
    this.clearAttachments();
  }

  setArea(area: IssueReportArea) {
    if (area === this.area) return;
    this.area = area;
    this.page = '';
  }

  // --- érvényesség ---

  get titleError(): string | null {
    if (!this.title.trim()) return 'Adj a hibának rövid címet.';
    if (this.title.length > this.titleMax) return `A cím legfeljebb ${this.titleMax} karakter lehet.`;
    return null;
  }

  get descriptionError(): string | null {
    if (!this.description.trim()) return 'Írd le, mit csináltál, mit vártál és mi történt.';
    if (this.description.length > this.descriptionMax) return `A leírás legfeljebb ${this.descriptionMax} karakter lehet.`;
    return null;
  }

  get valid(): boolean {
    return !this.titleError && !this.descriptionError;
  }

  // --- csatolmányok ---

  pickFiles(input: HTMLInputElement) {
    const files = Array.from(input.files ?? []);
    input.value = '';
    for (const file of files) {
      if (this.attachments.length >= this.maxFiles) {
        this.warn(`Legfeljebb ${this.maxFiles} fájl csatolható egy bejelentéshez.`);
        break;
      }
      if (!ISSUE_ATTACHMENT_TYPES.includes(file.type)) {
        this.warn(`„${file.name}”: csak JPEG, PNG, WebP kép vagy PDF csatolható.`);
        continue;
      }
      if (file.size > ISSUE_ATTACHMENT_MAX_BYTES) {
        this.warn(`„${file.name}” túl nagy (${this.mb(file.size)} MB); legfeljebb 5 MB lehet.`);
        continue;
      }
      this.attachments.push({ file, previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
    }
  }

  removeAttachment(a: PendingAttachment) {
    if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    this.attachments = this.attachments.filter((x) => x !== a);
  }

  mb(bytes: number): string {
    return (bytes / 1024 / 1024).toLocaleString('hu-HU', { maximumFractionDigits: 1 });
  }

  // --- beküldés ---

  submit() {
    this.submitted = true;
    if (!this.valid || this.busy) return;
    this.busy = true;
    const files = this.attachments.map((a) => a.file);
    this.issueReportService
      .create({
        area: this.area,
        page: this.page.trim() || null,
        title: this.title.trim(),
        description: this.description.trim(),
        severity: this.severity,
      })
      .subscribe({
        next: (created) => this.uploadAll(created.id, created.number, files),
        error: (error) => {
          this.busy = false;
          this.showError(error, 'A bejelentést nem sikerült beküldeni, próbáld újra.');
        },
      });
  }

  /** A csatolmányok egyenként, sorban mennek fel; a hibás fájl nevét és a szerver üzenetét összegyűjtjük. */
  private uploadAll(id: number, number: string, files: File[]) {
    if (!files.length) {
      this.finish(id, number, []);
      return;
    }
    const uploads = files.map((file) =>
      this.issueReportService.uploadAttachment(id, file).pipe(
        map(() => null as string | null),
        catchError((error) => of(`${file.name}: ${loyaltyErrorBody(error)?.friendlyMessage || 'a feltöltés nem sikerült'}`))
      )
    );
    const failed: string[] = [];
    concat(...uploads).subscribe({
      next: (fail) => {
        if (fail) failed.push(fail);
      },
      complete: () => this.finish(id, number, failed),
    });
  }

  private finish(id: number, number: string, failed: string[]) {
    this.busy = false;
    this.visible = false;
    this.clearAttachments();
    this.notificationService.show({
      title: 'Hibajelzés',
      type: MxNotificationSeverity.Success,
      message: `Köszönjük, a bejelentésed (${number}) megérkezett. Az állapotáról a harang alatt értesítünk.`,
    });
    if (failed.length) {
      this.notificationService.show({
        title: 'Csatolmány',
        type: MxNotificationSeverity.Warn,
        message: `A bejelentés létrejött, de nem minden fájl ment fel: ${failed.join('; ')}. A hiányzó képet a válaszunk után is elküldheted.`,
      });
    }
    this.dialogService.notifyCreated(id);
    if (!this.router.url.startsWith('/issue-reports')) {
      this.router.navigate(['/issue-reports'], { queryParams: { id } });
    }
  }

  // --- segédek ---

  /** A portál oldalai a menüből (a bejelentkezett szerepkör látható tételei), csoportcímmel. */
  private buildPageOptions(): PageOption[] {
    const options: PageOption[] = [];
    const seen = new Set<string>();
    const add = (label: string, routerLink: any[]) => {
      const value = routerLink.join('/').replace(/\/+/g, '/');
      if (seen.has(value)) return;
      seen.add(value);
      options.push({ label, value });
    };
    for (const m of (this.menuService.menu || []).filter((x: any) => x.visible !== false)) {
      if (m.items) {
        for (const c of m.items.filter((x: any) => x.visible !== false && x.routerLink)) {
          add(`${this.i18NextPipe.transform('page:' + m.label)} · ${this.i18NextPipe.transform('page:' + c.label)}`, c.routerLink);
        }
      } else if (m.routerLink) {
        add(this.i18NextPipe.transform('page:' + m.label), m.routerLink);
      }
    }
    return options;
  }

  /** Az útvonalhoz tartozó menüoldal neve; ha nincs ilyen (pl. részletoldal), maga az útvonal marad. */
  private pageLabelFor(path: string | null | undefined): string {
    if (!path) return '';
    const clean = path.split(/[?#]/)[0];
    const hit = [...this.pageOptions]
      .filter((o) => clean === o.value || (o.value !== '/' && clean.startsWith(o.value + '/')))
      .sort((a, b) => b.value.length - a.value.length)[0];
    if (!hit) return clean;
    return clean === hit.value ? hit.label : `${hit.label} (${clean})`;
  }

  private enumLabel(type: string, key: string): string {
    return this.i18NextPipe.transform(`enum:${type}.${key}`);
  }

  private clearAttachments() {
    for (const a of this.attachments) {
      if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    }
    this.attachments = [];
  }

  private warn(message: string) {
    this.notificationService.show({ title: 'Csatolmány', type: MxNotificationSeverity.Warn, message });
  }

  private showError(error: unknown, fallback: string) {
    if (typeof error === 'string') return;
    const body = loyaltyErrorBody(error);
    this.notificationService.show({ title: body?.errorTitle || 'Hibajelzés', type: MxNotificationSeverity.Warn, message: body?.friendlyMessage || fallback });
  }

  trackAttachment = (_: number, a: PendingAttachment) => a.file;
}
